/**
 * G-65 — the faces a gift note can be printed in.
 *
 * EVERY STACK ENDS IN A GENERIC FAMILY, and that is the rule that keeps this
 * safe rather than a matter of taste. CSS font fallback is per GLYPH: a stack
 * of `"FWHand", serif` draws Latin from the bundled face and anything it lacks
 * from Android's own Noto. Without the generic on the end, an uncovered
 * character prints as a tofu box — and at one bit a tofu box is a solid black
 * rectangle on a customer's card.
 *
 * The shop writes Spanish, Catalan, English, Russian and Ukrainian (owner,
 * 2026-09-12) — Latin and Cyrillic. Android's built-in fonts cover both, so
 * "Plain" and "Serif" need no download at all.
 */

import type { PlainKey } from "../i18n/types";

export interface NoteFont {
  id: string;
  /**
   * The message KEY, not the words. A `label: "Plain"` here would be English
   * frozen at import — this array is built once, before any language is
   * active — and it is data rather than markup, so the ratchet's heuristic
   * never saw it either: a one-word literal in an object has no whitespace to
   * catch on. The owner found these three on the real screen (2026-09-21).
   */
  labelKey: PlainKey;
  stack: string;
  /**
   * The weight to draw at. Not decoration on the handwriting face: its strokes
   * measure ~1.9 dots at weight 400 and 30 px, which is where a 1-bit head
   * starts dropping them to a ragged single row. At 700 they are ~2.7-3.0.
   */
  weight: number;
  /**
   * Smallest size auto-fit may shrink to. Below this the face stops printing
   * cleanly, so a long message overflows with a warning instead of arriving
   * unreadable.
   */
  minPx?: number;
  /** Must be loaded before measuring — see `fontsReady` on the note screen. */
  webFont?: string;
}

export const NOTE_FONTS: NoteFont[] = [
  {
    id: "plain",
    labelKey: "note.fontPlain",
    // The receipt's stack. Nothing to download, and Android's Noto fallback
    // behind it covers every script this shop writes in.
    stack: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    weight: 400,
  },
  {
    id: "serif",
    // NOT called "Times New Roman": Android has no such font, so this resolves
    // to Noto Serif. Promising a face the device does not have is how you get
    // a florist reporting that the font picker "does nothing".
    labelKey: "note.fontSerif",
    stack: "'Times New Roman', Times, serif",
    weight: 400,
  },
  {
    id: "hand",
    // Named for what it IS, not what it is called. A florist choosing lettering
    // does not care that this is Caveat, and the name would be the only thing
    // on the screen nobody could act on.
    labelKey: "note.fontHand",
    /**
     * Caveat 2.000 (SIL OFL 1.1), instanced at weight 700 and subset to the
     * scripts this shop writes. Its Cyrillic was drawn by Cyreal in Moscow
     * rather than derived from the Latin, which a Russian-speaking customer
     * would notice; Spanish, Catalan and Ukrainian are complete too — all
     * checked glyph by glyph against the shipped file, not against a subset
     * list.
     *
     * `cursive` on the end is the safety net every stack here carries: a
     * character the face lacks falls through to Android's own hand rather
     * than printing as a solid black tofu box.
     */
    stack: "'FW Hand', cursive",
    weight: 700,
    // 700 measures ~2.7 dots at 30 px, which is the floor for a 1-bit head.
    minPx: 30,
    webFont: "FW Hand",
  },
];

export const DEFAULT_NOTE_FONT = NOTE_FONTS[0];

export function noteFont(id: string): NoteFont {
  return NOTE_FONTS.find((f) => f.id === id) ?? DEFAULT_NOTE_FONT;
}
